import { useState, useEffect, useCallback } from 'react';
import { FootballEvent, FootballGame } from '@/types';
import {
  getSofaAllFootballByDate,
  getSofaAllFootballLive,
  getSofaMatchIncidents,
  sofaIncidentToFootballEvent,
} from '@/services/api/sofascore';
import { getESPNAllFootballGamesByDate } from '@/services/api/espn-all';
import { get365FootballGameDetail, get365FootballGames } from '@/services/api/scores365';
import { livePoller } from '@/services/livePoller';
import { teamNameKey } from '@/utils/teamMatching';
import { gameCache } from '@/store/gameCache';

export interface LeagueGroup {
  id: string;
  name: string;
  country: string;
  flag: string;
  logo: string;
  games: FootballGame[];
  hasLive: boolean;
}

export interface RegionGroup {
  id: string;
  name: string;
  tag: string;
  priority: number;
  gamesCount: number;
  liveCount: number;
  leagueGroups: LeagueGroup[];
}

interface RegionMeta {
  id: string;
  name: string;
  tag: string;
  priority: number;
}

const REGION_BY_COUNTRY: Record<string, RegionMeta> = {
  World: { id: 'world', name: 'Internacional', tag: 'WORLD', priority: 1 },
  International: { id: 'world', name: 'Internacional', tag: 'WORLD', priority: 1 },
  Europe: { id: 'europe', name: 'Europa', tag: 'EURO', priority: 4 },
  'South America': { id: 'south-america', name: 'America do Sul', tag: 'SUL', priority: 3 },
  'North America': { id: 'north-america', name: 'America do Norte', tag: 'NORTE', priority: 6 },
  Asia: { id: 'asia', name: 'Asia', tag: 'ASIA', priority: 7 },
  Africa: { id: 'africa', name: 'Africa', tag: 'AFR', priority: 8 },
  Brazil: { id: 'brazil', name: 'Brasil', tag: 'BR', priority: 2 },
  Argentina: { id: 'argentina', name: 'Argentina', tag: 'ARG', priority: 5 },
  England: { id: 'england', name: 'Inglaterra', tag: 'ENG', priority: 5 },
  Spain: { id: 'spain', name: 'Espanha', tag: 'ESP', priority: 5 },
  Italy: { id: 'italy', name: 'Italia', tag: 'ITA', priority: 5 },
  Germany: { id: 'germany', name: 'Alemanha', tag: 'ALE', priority: 5 },
  France: { id: 'france', name: 'Franca', tag: 'FRA', priority: 5 },
  Portugal: { id: 'portugal', name: 'Portugal', tag: 'POR', priority: 5 },
  Netherlands: { id: 'netherlands', name: 'Holanda', tag: 'HOL', priority: 5 },
  USA: { id: 'usa', name: 'Estados Unidos', tag: 'USA', priority: 6 },
  Mexico: { id: 'mexico', name: 'Mexico', tag: 'MEX', priority: 6 },
};

function isWorldCupLeague(name: string): boolean {
  const n = name.toLowerCase();
  return n.includes('world cup') || n.includes('fifa world cup') || n.includes('copa do mundo');
}

function regionForLeague(country: string, leagueName: string): RegionMeta {
  if (isWorldCupLeague(leagueName)) {
    return { id: 'world-cup', name: 'Copa do Mundo', tag: 'COPA', priority: 0 };
  }
  if (REGION_BY_COUNTRY[country]) return REGION_BY_COUNTRY[country];
  if (!country) return { id: 'other', name: 'Outras regioes', tag: 'OUT', priority: 99 };

  return {
    id: country.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
    name: country,
    tag: country.slice(0, 3).toUpperCase(),
    priority: 20,
  };
}

function isLiveGame(game: FootballGame): boolean {
  return game.status === 'live' || game.status === 'halftime';
}

function dateKeyFromDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function todayKey(): string {
  return dateKeyFromDate(new Date());
}

async function enrichLiveGamesWithEvents(games: FootballGame[]): Promise<FootballGame[]> {
  const liveGames = games
    .filter((game) => isLiveGame(game) && /^\d+$/.test(game.id))
    .slice(0, 24);

  if (liveGames.length === 0) return games;

  const entries: Array<readonly [string, FootballEvent[]]> = await Promise.all(
    liveGames.map(async (game) => {
      try {
        const incidents = await getSofaMatchIncidents(Number(game.id));
        const events = incidents
          .map(sofaIncidentToFootballEvent)
          .filter((event): event is FootballEvent => event !== null)
          .sort((a, b) => a.minute - b.minute);
        return [game.id, events] as const;
      } catch {
        return [game.id, [] as FootballEvent[]] as const;
      }
    })
  );

  const byGame = new Map(entries.filter(([, events]) => events.length > 0));
  if (byGame.size === 0) return games;

  return games.map((game) =>
    byGame.has(game.id) ? { ...game, events: byGame.get(game.id) } : game
  );
}

async function enrich365LiveGamesWithEvents(games: FootballGame[]): Promise<FootballGame[]> {
  const live365Games = games
    .filter((game) => (isLiveGame(game) || game.status === 'finished') && game.id.startsWith('365:'))
    .slice(0, 24);

  if (live365Games.length === 0) return games;

  const entries = await Promise.all(
    live365Games.map(async (game) => {
      try {
        const detail = await get365FootballGameDetail(game.id);
        return [game.id, detail] as const;
      } catch {
        return [game.id, null] as const;
      }
    })
  );

  const byId = new Map(
    entries.filter((entry): entry is readonly [string, FootballGame] => {
      const detail = entry[1];
      return !!detail && ((detail.events?.length ?? 0) > 0 || (detail.timeline?.length ?? 0) > 0);
    })
  );

  if (byId.size === 0) return games;
  return games.map((game) => byId.get(game.id) ?? game);
}

function dedupeKey(game: FootballGame): string {
  const date = game.startTime.slice(0, 13);
  const home = teamNameKey(game.homeTeam.name || game.homeTeam.shortName);
  const away = teamNameKey(game.awayTeam.name || game.awayTeam.shortName);
  return `${date}:${home}:${away}`;
}

function shouldPrefer365(game: FootballGame): boolean {
  return game.status === 'live' || game.status === 'halftime' || game.status === 'finished';
}

function mergeFootballSources(
  espnGames: FootballGame[],
  sofaGames: FootballGame[],
  scores365Games: FootballGame[]
): FootballGame[] {
  const byKey = new Map<string, FootballGame>();

  for (const game of espnGames) {
    byKey.set(dedupeKey(game), game);
  }

  for (const game of sofaGames) {
    const key = dedupeKey(game);
    const current = byKey.get(key);
    if (!current) {
      byKey.set(key, game);
      continue;
    }

    const currentHasEvents = (current.events?.length ?? 0) > 0;
    const nextHasEvents = (game.events?.length ?? 0) > 0;
    if (!currentHasEvents && nextHasEvents) {
      byKey.set(key, game);
    }
  }

  for (const game of scores365Games) {
    const key = dedupeKey(game);
    const current = byKey.get(key);
    if (!current) {
      byKey.set(key, game);
      continue;
    }

    if (shouldPrefer365(game)) {
      byKey.set(key, {
        ...game,
        events: game.events?.length ? game.events : current.events,
        timeline: game.timeline?.length ? game.timeline : current.timeline,
        statistics: game.statistics?.length ? game.statistics : current.statistics,
        homeTeam: {
          ...game.homeTeam,
          logo: game.homeTeam.logo || current.homeTeam.logo,
        },
        awayTeam: {
          ...game.awayTeam,
          logo: game.awayTeam.logo || current.awayTeam.logo,
        },
      });
      continue;
    }

    byKey.set(key, {
      ...current,
      score: current.score.home || current.score.away ? current.score : game.score,
      minute: current.minute ?? game.minute,
      homeTeam: {
        ...current.homeTeam,
        logo: current.homeTeam.logo || game.homeTeam.logo,
      },
      awayTeam: {
        ...current.awayTeam,
        logo: current.awayTeam.logo || game.awayTeam.logo,
      },
    });
  }

  return Array.from(byKey.values());
}

/**
 * Cobertura completa de futebol (todos os jogos do mundo, qualquer liga),
 * com placar ao vivo — equivalente à página "jogos de hoje" de uma casa de
 * apostas, mas via SofaScore (gratuito, estável, sem scraping).
 *
 * @param enabled quando false, não busca nada (evita polling desnecessário)
 */
export function useAllFootballGames(enabled: boolean = true, selectedDate: string = todayKey()) {
  const [games, setGames] = useState<FootballGame[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchGames = useCallback(async () => {
    try {
      setLoading(true);
      const isSelectedToday = selectedDate === todayKey();

      // Busca ESPN + SofaScore: ESPN replica o placar exibido no portal,
      // SofaScore cobre competicoes extras e traz incidentes ao vivo.
      const [espnGames, todayGames, liveGames, scores365Games] = await Promise.all([
        getESPNAllFootballGamesByDate(selectedDate).catch(() => [] as FootballGame[]),
        getSofaAllFootballByDate(selectedDate).catch(() => [] as FootballGame[]),
        isSelectedToday ? getSofaAllFootballLive().catch(() => [] as FootballGame[]) : Promise.resolve([] as FootballGame[]),
        isSelectedToday ? get365FootballGames().catch(() => [] as FootballGame[]) : Promise.resolve([] as FootballGame[]),
      ]);

      // Mescla: jogos ao vivo têm o placar mais fresco e prevalecem
      const liveIds = new Set(liveGames.map((g) => g.id));
      const sofaMerged = [
        ...liveGames,
        ...todayGames.filter((g) => !liveIds.has(g.id)),
      ];
      const sofaWithEvents = await enrichLiveGamesWithEvents(sofaMerged);
      const scores365WithEvents = await enrich365LiveGamesWithEvents(scores365Games);
      const merged = mergeFootballSources(espnGames, sofaWithEvents, scores365WithEvents);

      setGames(merged);
      gameCache.setMany(merged);
      setError(null);
      setLastUpdated(new Date());
      livePoller.notifyGameUpdates(merged).catch(() => {});
    } catch (e: any) {
      console.error('[AllFootball] Erro:', e.message);
      setError(e.message ?? 'Erro ao buscar jogos');
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  const hasLive = games.some((g) => g.status === 'live' || g.status === 'halftime');
  const refreshInterval = hasLive ? 30_000 : 90_000;

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    fetchGames();
    const timer = setInterval(fetchGames, refreshInterval);
    return () => clearInterval(timer);
  }, [enabled, fetchGames, refreshInterval]);

  // Derivados
  const liveGames = games.filter((g) => g.status === 'live' || g.status === 'halftime');
  const scheduledGames = games.filter((g) => g.status === 'scheduled');
  const finishedGames = games.filter((g) => g.status === 'finished');

  // Agrupa por liga; ordena: ligas com jogo ao vivo primeiro, depois por nº de jogos
  const byLeague = games.reduce<Record<string, LeagueGroup>>((acc, g) => {
    const lid = g.league.id;
    if (!acc[lid]) {
      acc[lid] = {
        id: lid,
        name: g.league.name,
        country: g.league.country,
        flag: g.league.flag ?? '⚽',
        logo: g.league.logo,
        games: [],
        hasLive: false,
      };
    }
    acc[lid].games.push(g);
    if (g.status === 'live' || g.status === 'halftime') acc[lid].hasLive = true;
    return acc;
  }, {});

  const leagueGroups: LeagueGroup[] = Object.values(byLeague)
    .map((grp) => ({
      ...grp,
      games: grp.games.sort((a, b) => {
        // ao vivo no topo, depois por horário
        const order = (s: string) => (s === 'live' || s === 'halftime' ? 0 : s === 'scheduled' ? 1 : 2);
        const d = order(a.status) - order(b.status);
        return d !== 0 ? d : a.startTime.localeCompare(b.startTime);
      }),
    }))
    .sort((a, b) => {
      if (a.hasLive !== b.hasLive) return a.hasLive ? -1 : 1;
      return b.games.length - a.games.length;
    });

  const regionGroups: RegionGroup[] = Object.values(
    leagueGroups.reduce<Record<string, RegionGroup>>((acc, league) => {
      const meta = regionForLeague(league.country, league.name);
      if (!acc[meta.id]) {
        acc[meta.id] = {
          ...meta,
          gamesCount: 0,
          liveCount: 0,
          leagueGroups: [],
        };
      }

      acc[meta.id].leagueGroups.push(league);
      acc[meta.id].gamesCount += league.games.length;
      acc[meta.id].liveCount += league.games.filter(isLiveGame).length;
      return acc;
    }, {})
  )
    .map((region) => ({
      ...region,
      leagueGroups: region.leagueGroups.sort((a, b) => {
        if (a.hasLive !== b.hasLive) return a.hasLive ? -1 : 1;
        return b.games.length - a.games.length || a.name.localeCompare(b.name);
      }),
    }))
    .sort((a, b) => {
      if (a.priority !== b.priority) return a.priority - b.priority;
      if (a.liveCount !== b.liveCount) return b.liveCount - a.liveCount;
      return b.gamesCount - a.gamesCount || a.name.localeCompare(b.name);
    });

  return {
    games,
    liveGames,
    scheduledGames,
    finishedGames,
    leagueGroups,
    regionGroups,
    loading,
    error,
    refresh: fetchGames,
    lastUpdated,
  };
}
